import { atom, getDefaultStore } from 'jotai'

const NSEC_RECORD_KEY = 's:nsecGeneratedHere'

export const PROFILE_EDIT_PROMPT_DELAY_SECONDS = 20 * 60 * 60

export type TNsecRecord = {
  createdAt: number
  profileEdited?: boolean
  activity: string[]
}

function loadRecord(): TNsecRecord | null {
  try {
    const raw = localStorage.getItem(NSEC_RECORD_KEY)
    if (!raw) return null
    const record = JSON.parse(raw) as Partial<TNsecRecord>
    return {
      createdAt: record.createdAt!,
      profileEdited: record.profileEdited,
      activity: Array.isArray(record.activity) ? record.activity : []
    }
  } catch {
    return null
  }
}

function saveRecord(record: TNsecRecord | null) {
  if (!record) localStorage.removeItem(NSEC_RECORD_KEY)
  else localStorage.setItem(NSEC_RECORD_KEY, JSON.stringify(record))
}

export function recordNsecGenerated() {
  saveRecord({ createdAt: Math.floor(Date.now() / 1000), activity: [] })
}

export function markProfileEdited(): boolean {
  const record = loadRecord()
  if (!record || record.profileEdited) return false
  record.profileEdited = true
  saveRecord(record)
  return true
}

export function hasUnsavedNsec(): boolean {
  return !!loadRecord()
}

export function shouldPromptAfterProfileEdit(): boolean {
  const record = loadRecord()
  if (!record?.profileEdited) return false
  return Math.floor(Date.now() / 1000) - record.createdAt > PROFILE_EDIT_PROMPT_DELAY_SECONDS
}

export function clearNsecRecord() {
  saveRecord(null)
}

// adds the local day of the given event timestamp to the stored list of unique
// days on which notes were published; returns true when the count of unique
// days hits a multiple of 5 for the first time
export function recordNotePublishedDay(eventCreatedAt: number): boolean {
  try {
    const record = loadRecord()
    if (!record) return false

    const date = new Date(eventCreatedAt * 1000)
    const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`
    if (record.activity.includes(day)) return false
    record.activity.push(day)
    saveRecord(record)
    return record.activity.length % 5 === 0
  } catch {
    return false
  }
}

export type TSaveNsecModalMode = 'normal' | 'logout'
export const saveNsecModalAtom = atom<{ open: boolean; mode: TSaveNsecModalMode }>({
  open: false,
  mode: 'normal'
})

export function openSaveNsecModal(mode: TSaveNsecModalMode = 'normal') {
  getDefaultStore().set(saveNsecModalAtom, { open: true, mode })
}
